<template>
  <p v-if="store.builderConfig?.conditionalLogic?.length" class="mb-3">
    <UIAlert id="warning-message" type="warning">
      <template #content> {{ t("common.conditionalLogicAlertOnSubmit") }}</template>
    </UIAlert>
  </p>
  <UISelect
    id="submit"
    :value="submitOption"
    :options="options"
    @update:value="selectionSubmitType"
  />
  <UIInput
    v-if="submitOption === 'url'"
    id="submitMessage"
    :model-value="store.formAction.redirectUrl"
    :placeholder="store.formAction.redirectUrl"
    @update:model-value="updateSubmitMessage"
    @blur="store.addToHistory()"
  />
  <p v-if="submitOption === 'url'" class="mt-2 text-sm font-normal text-gray-600">
    {{ t("common.addUrlLike") }} <b>example.com</b>, <b>https://example.com</b>,
    <b>example.com/path</b> {{ t("common.or") }} <b>https://www.example.com/path</b>.
  </p>
  <p v-if="submitOption === 'order'" class="mt-2 text-sm font-normal text-gray-600">
    {{ t("common.payment.product.orderConfirmationWarning") }}
  </p>
  <p v-if="submitOption === 'quizResult'" class="mt-2 text-sm font-normal text-gray-600">
    {{ t("quiz.quizResult.quizResultAlertMessage") }}
  </p>
  <section v-if="submitOption === 'message'" class="w-100 flex flex-col gap-3">
    <RichTextEditor v-model:modelValue="thankYouMessage" :is-submit-message="true"></RichTextEditor>
    <MessageStyling></MessageStyling>
  </section>
</template>

<script setup lang="ts">
import { useAppStore } from "@/store/app";
import { UIAlert, UIInput, UISelect } from "@gohighlevel/ghl-ui";
import { computed, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { PRODUCT_TYPE } from "../../../../../form-survey/src/helper/const";
import RichTextEditor from "@/components/text-editor/index.vue";
import MessageStyling from "./MessageStyling.vue";
const { t } = useI18n();

const store = useAppStore();

const props = defineProps({
  product: {
    type: String,
    default: PRODUCT_TYPE.FORM,
  },
});
const formStoreSubmitMessageStyle = store.getFormSubmitMessageStyle;
const formSubmitMessageStyle = reactive(formStoreSubmitMessageStyle);

const options = computed(() => {
  const list = [
    { label: t("common.openUrl"), value: "url" },
    { label: t("common.message"), value: "message" },
    {
      label: t("common.payment.product.orderConfirmation"),
      value: "order",
      disabled: props.product !== PRODUCT_TYPE.FORM,
    },
  ];

  if (props.product === PRODUCT_TYPE.QUIZ) {
    list.push({ label: t("quiz.quizResult.resultTemplate"), value: "quizResult" });
  }

  return list;
});

const getActionTypeValue = (actionType: string) => {
  switch (actionType) {
    case "1":
      return "url";
    case "2":
      return "message";
    case "3":
      return "order";
    case "4":
      return "quizResult";
    default:
      return "message";
  }
};

watch(
  () => store.formAction.actionType,
  async (newValue: any) => {
    await (submitOption.value = getActionTypeValue(String(newValue)));
  }
);

const submitOption = ref(getActionTypeValue(String(store.formAction.actionType)));

const selectionSubmitType = (type) => {
  if (type === "url") {
    store.formAction.actionType = "1";
    submitOption.value = "url";
  }
  if (type === "message") {
    store.formAction.actionType = "2";
    submitOption.value = "message";
  }
  if (type === "order") {
    store.formAction.actionType = "3";
    submitOption.value = "order";
  }
  if (type === "quizResult") {
    store.formAction.actionType = "4";
    submitOption.value = "quizResult";
  }
  store.anyUnsavedChanges = true;
  store.addToHistory();
};

const thankYouMessage = computed({
  get() {
    return store.formAction.thankyouText;
  },
  set(value: string) {
    store.formAction.thankyouText = value;
    store.anyUnsavedChanges = true;
  },
});

const updateSubmitMessage = (value: string) => {
  store.formAction.redirectUrl = value;
  store.anyUnsavedChanges = true;
};
</script>
